import * as CD from 'app/cd/models/essette-api';
import { bindable } from 'aurelia-typed-observable-plugin';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { autoinject } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection, StandardLoadOptions } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { ProviderService } from 'app/cd/services/provider-service';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { Toaster } from 'app/shared/lib/toaster';
import { IPopup } from 'app/shared/elements/popup-base';
import { SaveContactCustomElement } from 'app/cd/components/providers/save-contact';
import { validate } from 'app/shared/elements/ess-rule';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';


@autoinject()
export class ContactInfoGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ changeHandler: 'reload' })
    public providerId: string | null = null;

    @bindable.stringNull({ changeHandler: 'reload' })
    public locationId: string | null = null;

    @bindable.boolean()
    public readonly: boolean = true;

    @bindable.boolean({ changeHandler: 'reload' })
    public includeInactive: boolean = false;

    protected contacts: ReadonlyArray<CD.ProviderContact> | null = [];
    protected selectedContact: CD.ProviderContact | null = null;

    protected sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 5;

    protected modalTitle: string = '';
    protected validateForm = validate;
    protected $isValid: boolean = false;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $modal!: IPopup;
    protected $saveContact!: SaveContactCustomElement;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly providerService: ProviderService,
        private readonly toaster: Toaster,
        ...rest: any[]) {

        super(...rest);
    }


    ////****************** Events ***************************************************////
    protected async reload(): Promise<void> {
        await this.loadContacts();
    }


    ////****************** Functions ************************************************////
    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    protected addContact(): void {
        if(this.$modal && this.$saveContact) {
            const contact: CD.ProviderContact = {
                location: {
                    id: this.locationId
                }
            };
            this.modalTitle = 'ui:labels.new';
            this.$saveContact.LoadProviderContact(contact);
            this.$modal.open();
        }
    }

    protected editContact(contact: CD.ProviderContact): void {
        if(this.$modal && this.$saveContact) {
            this.modalTitle = !!contact?.id ? 'ui:actions.edit' : 'ui:labels.new';
            this.$saveContact.LoadProviderContact(contact);
            this.$modal.open();
        }
    }

    protected handleContactSaved(event: FormSuccessCustomEvent) {
        this.$modal.close();
        this.reload();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadContacts(): Promise<void> {
        if(!this.locationId) return;

        const options: StandardLoadOptions = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: this.includeInactive
        };

        const includes: string[] = [
            'address.mailing',
            'phoneNumber.primary',
            'phoneNumber.fax',
            'address.mailing.area'
        ];

        try {
            this.state.beginLoad();

            const response = await this.providerService.loadProviderContactsByLocation(this.locationId, options, includes);
            this.contacts = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async deactivateContact(contact: CD.ProviderContact): Promise<void> {
        if(!contact.id) return;

        const result = await confirmation.showDeactivate('cd:providerContact.$', 1, false);
        if(result.dialogResponse === confirmation.options.CANCEL) { return; }

        if(!(result.dialogResponse === confirmation.options.OK)) {
            return;
        }

        try {
            this.state.beginSave();
            const response = await this.providerService.activateProviderContact(contact.id, false);

            if(response.isSuccessful) {
                this.reload();
            }
            this.toaster.standard.activate('cd:providerContact.$', false, response.isSuccessful);
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

    @apiCall
    protected async reactivateContact(contact: CD.ProviderContact): Promise<void> {
        if(!contact.id) return;

        try {
            this.state.beginSave();
            const response = await this.providerService.activateProviderContact(contact.id, true);

            if(response.isSuccessful) {
                this.reload();
            }
            this.toaster.standard.activate('cd:providerContact.$', true, response.isSuccessful);
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }
}
